import { isEqual } from 'lodash';
import { useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import Typography from '@mui/material/Typography';
import { Form, Formik, FormikValues } from 'formik';

import { Config } from 'config';
import { mapQuery } from 'utils';
import { Details } from 'components/details';
import { useCurrentQuery } from 'store/api/tenants';

import { Icon } from '../icons';

import { AuthLayoutProps } from './types';

export function AuthLayout<Value extends FormikValues>({
  title,
  children,
  onSubmit,
  logoHidden,
  initialValues,
  lowerTitleSize,
  containerClassName,
  ...formikProps
}: AuthLayoutProps<Value>) {
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const valuesRef = useRef<Value>(initialValues);

  const resetMessages = () => {
    setErrorMessage('');
    setSuccessMessage('');
  };

  const onChangeValues = (values: Value) => {
    const isChanged = !isEqual(values, valuesRef.current);
    valuesRef.current = values;
    if (isChanged) {
      resetMessages();
    }
  };

  const { data: tenant, ...props } = useCurrentQuery(undefined, {
    skip: !Config.showTenantLogo,
  });

  const renderedMessage = errorMessage || successMessage;

  return (
    <Details {...mapQuery(props)} loaderFullScreen>
      <div className="flex h-screen w-screen bg-[url('/images/auth-background.jpg')] lg:bg-cover lg:bg-center">
        <div className="shadow-blue-shadow06 z-10 flex h-full w-full flex-col overflow-y-auto bg-white/90 backdrop-blur-[38px] lg:w-1/2 lg:rounded-r-2xl">
          <div className="relative flex h-full flex-col justify-start overflow-y-auto pt-4 pr-4 pb-4 pl-4 lg:justify-center lg:pt-18 lg:pr-[16.1%] lg:pb-18 lg:pl-[23.7%]">
            {!logoHidden && (
              <div className="flex gap-2 lg:absolute lg:top-16 lg:left-15">
                <img src="/logo.svg" alt="Seahl Logo" className="h-auto w-32" />
                {Config.showTenantLogo && tenant?.logo && (
                  <img
                    src={tenant.logo}
                    alt={tenant.name}
                    className="h-18 w-auto"
                  />
                )}
              </div>
            )}
            <div className="flex h-full w-full flex-wrap">
              <div
                className={twMerge(
                  'flex w-full flex-col pb-6 lg:mt-0 lg:mr-auto lg:ml-auto lg:max-w-106.5 lg:justify-center',
                  !logoHidden && `mt-10 lg:pt-20 lg:pb-20`,
                  containerClassName
                )}
              >
                <Typography
                  variant="h1"
                  className="mb-4 lg:mb-6"
                  sx={{
                    fontWeight: { xs: 600, lg: 700 },
                    fontSize: {
                      xs: '18px',
                      lg: lowerTitleSize ? '24px' : '40px',
                    },
                    lineHeight: {
                      xs: '28px',
                      lg: lowerTitleSize ? '32px' : '48px',
                    },
                  }}
                >
                  {title}
                </Typography>

                {renderedMessage && (
                  <div
                    className={twMerge(
                      'mb-3 flex items-center gap-3 rounded-xl px-4 py-3',
                      !!successMessage && 'bg-green-50',
                      !!errorMessage && 'bg-red-50'
                    )}
                  >
                    {errorMessage ? (
                      <Icon.Info
                        width={24}
                        height={24}
                        className="rotate-180 [&_path]:fill-red-500"
                      />
                    ) : (
                      <Icon.CheckCircle
                        width={24}
                        height={24}
                        className="[&_path]:fill-green-500"
                      />
                    )}
                    <Typography>{renderedMessage}</Typography>
                  </div>
                )}

                <div className="flex w-full items-center justify-center">
                  <Formik<Value>
                    initialValues={initialValues}
                    {...formikProps}
                    onSubmit={async (values, helpers) => {
                      resetMessages();
                      const result = onSubmit(values, helpers);
                      if (result instanceof Promise) {
                        const promiseResult = await result;
                        if (
                          promiseResult &&
                          typeof promiseResult === 'object'
                        ) {
                          if ('successMessage' in promiseResult) {
                            setSuccessMessage(promiseResult.successMessage);
                          }
                          if ('errorMessage' in promiseResult) {
                            setErrorMessage(promiseResult.errorMessage);
                          }
                        }
                        return promiseResult;
                      } else {
                        return result;
                      }
                    }}
                  >
                    {(props) => {
                      const render =
                        // @ts-ignore
                        children instanceof Function
                          ? children(props)
                          : children;

                      onChangeValues(props.values);

                      return (
                        <div className="w-full">
                          <Form>{render}</Form>
                        </div>
                      );
                    }}
                  </Formik>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Details>
  );
}
